/**
 * Componente Button
 *
 * Botón accesible con múltiples variantes y tamaños.
 */

import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/core/utils';
import type { ButtonVariant, Size } from '@/core/types';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: Size;
  isLoading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  fullWidth?: boolean;
}

const variantStyles: Record<ButtonVariant, string> = {
  primary:
    'bg-slate-200 text-slate-900 hover:bg-white focus:ring-slate-400',
  secondary:
    'bg-slate-800 text-slate-200 hover:bg-slate-700 focus:ring-slate-500',
  ghost:
    'bg-transparent text-slate-300 hover:bg-slate-800 focus:ring-slate-500',
  danger:
    'bg-red-600 text-white hover:bg-red-500 focus:ring-red-400',
  success:
    'bg-emerald-600 text-white hover:bg-emerald-500 focus:ring-emerald-400',
  survival:
    'bg-rose-600 text-white hover:bg-rose-500 focus:ring-rose-400',
  maintenance:
    'bg-amber-600 text-white hover:bg-amber-500 focus:ring-amber-400',
  growth:
    'bg-emerald-600 text-white hover:bg-emerald-500 focus:ring-emerald-400',
};

const sizeStyles: Record<Size, string> = {
  xs: 'px-2 py-1 text-xs gap-1',
  sm: 'px-3 py-1.5 text-sm gap-1.5',
  md: 'px-4 py-2 text-sm gap-2',
  lg: 'px-5 py-2.5 text-base gap-2',
  xl: 'px-6 py-3 text-lg gap-2.5',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      isLoading = false,
      leftIcon,
      rightIcon,
      fullWidth = false,
      className,
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || isLoading;

    return (
      <button
        ref={ref}
        className={cn(
          // Base styles
          'inline-flex items-center justify-center',
          'font-medium rounded-lg',
          'transition-all duration-200',
          'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-950',
          // Variant styles
          variantStyles[variant],
          // Size styles
          sizeStyles[size],
          // State styles
          isDisabled && 'opacity-50 cursor-not-allowed',
          fullWidth && 'w-full',
          className
        )}
        disabled={isDisabled}
        {...props}
      >
        {isLoading ? (
          <svg
            className="animate-spin h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
        ) : (
          leftIcon
        )}
        {children}
        {!isLoading && rightIcon}
      </button>
    );
  }
);

Button.displayName = 'Button';
